import { ConsoleState } from './store';
import { DEFAULT_ERROR_POLICY, ErrorPolicy, RouteDef, RouteIngress, TenantResolution, Vendor } from './types';

export const DEFAULT_INGRESS: RouteIngress = {
  path: '',
  method: 'POST',
  contentType: 'application/json',
  auth: { kind: 'none' },
  tenant: { kind: 'none' },
  maxBodyBytes: 1_000_000,
};

/** Route settings win over its group's sign-in, then vendor defaults, then the built-in defaults. */
export const resolveIngress = (state: ConsoleState, route: RouteDef): RouteIngress => {
  const vendor = state.vendors.find((candidate) => candidate.id === route.vendorId);
  const group = state.groups.find((candidate) => candidate.id === route.groupId);

  return {
    ...DEFAULT_INGRESS,
    ...vendor?.ingressDefaults,
    ...(group?.authSchemeId ? { authSchemeId: group.authSchemeId } : {}),
    ...route.ingress,
    auth: { ...DEFAULT_INGRESS.auth, ...vendor?.ingressDefaults?.auth, ...route.ingress?.auth },
    tenant: { ...DEFAULT_INGRESS.tenant, ...vendor?.ingressDefaults?.tenant, ...route.ingress?.tenant },
  };
};

/**
 * Settings for an endpoint with its address left as Casa's API docs give it. Sending the same
 * address back is fine; a different one is refused, because the docs are the only place it changes.
 */
export const keepDocsAddress = (
  state: ConsoleState,
  route: RouteDef,
  asked: Partial<RouteIngress>,
): Partial<RouteIngress> => {
  const { path, method, ...rest } = asked;
  const docs = resolveIngress(state, route);
  if ((path !== undefined && path !== docs.path) || (method !== undefined && method !== docs.method))
    throw new Error("The address comes from Casa's API docs and can't be changed here.");
  return rest;
};

export const resolveErrorPolicy = (state: ConsoleState, route: RouteDef): ErrorPolicy => {
  const vendor = state.vendors.find((candidate) => candidate.id === route.vendorId);

  return { ...DEFAULT_ERROR_POLICY, ...vendor?.errorPolicy, ...route.errorPolicy };
};

export interface ConfigIssue {
  field: string;
  message: string;
}

const PATH_PATTERN = /^\/[A-Za-z0-9\-._~/{}]*$/;
const HEADER_PATTERN = /^[A-Za-z0-9-]+$/;
const SECRET_LIKE = /^(?:[A-Za-z0-9+/=_-]{24,}|.*(?:password|secret|token)=.*)$/i;

export const validateIngress = (state: ConsoleState, route: RouteDef, ingress: RouteIngress): ConfigIssue[] => {
  const issues: ConfigIssue[] = [];
  const vendor = state.vendors.find((candidate) => candidate.id === route.vendorId);

  if (!ingress.path || !PATH_PATTERN.test(ingress.path)) {
    issues.push({ field: 'path', message: 'The path must start with / and contain no spaces.' });
  }
  if (ingress.path.includes('//')) {
    issues.push({ field: 'path', message: 'The path must not contain an empty segment.' });
  }
  /* The runtime mounts one listener per first path segment, so a placeholder there mounts nothing. */
  if (/^\/\{/.test(ingress.path ?? '')) {
    issues.push({ field: 'path', message: 'The first part of the path must be a fixed word, not a {placeholder}.' });
  }

  /* Vendors may share an address, and the caller's key picks the route; one vendor may not use it twice. */
  const clash = state.routes.find(
    (candidate) =>
      candidate.id !== route.id &&
      candidate.vendorId === route.vendorId &&
      resolveIngress(state, candidate).path === ingress.path,
  );
  if (clash) {
    issues.push({ field: 'path', message: `${clash.name} already listens on ${ingress.path}.` });
  }

  if (ingress.method !== 'POST' && ingress.method !== 'PUT') {
    issues.push({ field: 'method', message: 'A vendor may only POST or PUT.' });
  }
  if (!ingress.contentType.includes('/')) {
    issues.push({ field: 'contentType', message: 'The content type must be a media type.' });
  }
  if (ingress.maxBodyBytes !== undefined && (ingress.maxBodyBytes < 1024 || ingress.maxBodyBytes > 50_000_000)) {
    issues.push({ field: 'maxBodyBytes', message: 'The body limit must be between 1KB and 50MB.' });
  }

  if (ingress.auth.kind === 'api-key') {
    if (!ingress.auth.headerName || !HEADER_PATTERN.test(ingress.auth.headerName)) {
      issues.push({ field: 'auth.headerName', message: 'An api-key needs the header it arrives in.' });
    }
  }
  if (ingress.auth.kind !== 'none' && !ingress.auth.secretRef) {
    issues.push({ field: 'auth.secretRef', message: 'Name the secret the runtime should resolve.' });
  }
  if (ingress.auth.secretRef && SECRET_LIKE.test(ingress.auth.secretRef)) {
    issues.push({
      field: 'auth.secretRef',
      message: 'This looks like a credential. Store the secret in the runtime and name it here instead.',
    });
  }

  issues.push(...validateTenantResolution(vendor, ingress.tenant));

  return issues;
};

export const validateTenantResolution = (vendor: Vendor | undefined, tenant: TenantResolution): ConfigIssue[] => {
  const issues: ConfigIssue[] = [];

  if (tenant.kind === 'header' && !HEADER_PATTERN.test(tenant.name ?? '')) {
    issues.push({ field: 'tenant.name', message: 'Name the header the tenant arrives in.' });
  }
  if (tenant.kind === 'query' && !/^[A-Za-z0-9_-]+$/.test(tenant.name ?? '')) {
    issues.push({ field: 'tenant.name', message: 'Name the query parameter the tenant arrives in.' });
  }
  if (tenant.kind === 'body' && !/^[A-Za-z0-9_.[\]]+$/.test(tenant.path ?? '')) {
    issues.push({ field: 'tenant.path', message: 'Give the field in the body that carries the tenant.' });
  }
  if (tenant.kind === 'fixed') {
    if (!tenant.tenantId) {
      issues.push({ field: 'tenant.tenantId', message: 'Choose the tenant this route always belongs to.' });
    } else if (vendor && !vendor.tenantIds.includes(tenant.tenantId)) {
      issues.push({
        field: 'tenant.tenantId',
        message: `${tenant.tenantId} is not connected to ${vendor.name}. Connect it first.`,
      });
    }
  }
  if (tenant.kind === 'none' && vendor && vendor.tenantIds.length > 1) {
    issues.push({
      field: 'tenant',
      message: `${vendor.name} serves ${vendor.tenantIds.length} tenants, so a request must say which one.`,
    });
  }

  return issues;
};

export const validateErrorPolicy = (policy: ErrorPolicy): ConfigIssue[] => {
  const issues: ConfigIssue[] = [];

  if (policy.rejectStatus < 400 || policy.rejectStatus > 499) {
    issues.push({ field: 'rejectStatus', message: 'A rejected payload is the sender’s fault, so use a 4xx.' });
  }
  if (policy.maxRetries < 0 || policy.maxRetries > 20) {
    issues.push({ field: 'maxRetries', message: 'Retries must be between 0 and 20.' });
  }
  if (policy.retryDelayMs < 10 || policy.retryDelayMs > 60_000) {
    issues.push({ field: 'retryDelayMs', message: 'The first retry delay must be between 10ms and 60s.' });
  }
  if (policy.backoffMultiplier < 1 || policy.backoffMultiplier > 10) {
    issues.push({ field: 'backoffMultiplier', message: 'The backoff multiplier must be between 1 and 10.' });
  }
  if (policy.maxRetryDelayMs < policy.retryDelayMs) {
    issues.push({ field: 'maxRetryDelayMs', message: 'The retry ceiling cannot be below the first delay.' });
  }
  if (policy.captureRejects && !policy.rejectTarget) {
    issues.push({ field: 'rejectTarget', message: 'Say where rejected payloads are captured.' });
  }
  if (policy.onExhausted === 'dlq' && !policy.dlqTarget) {
    issues.push({ field: 'dlqTarget', message: 'Say where exhausted deliveries go.' });
  }
  if (policy.onExhausted === 'drop') {
    issues.push({
      field: 'onExhausted',
      message: 'Dropping loses accepted messages. Choose dlq or spool unless that is genuinely intended.',
    });
  }

  return issues;
};

/**
 * The endpoints a vendor is told about, with the tenants each one may resolve.
 * This is what gets handed over during onboarding.
 */
export const vendorEndpoints = (state: ConsoleState, vendorId: string) => {
  const vendor = state.vendors.find((candidate) => candidate.id === vendorId);

  return state.routes
    .filter((route) => route.vendorId === vendorId)
    .map((route) => {
      const ingress = resolveIngress(state, route);
      const group = state.groups.find((candidate) => candidate.id === route.groupId);

      return {
        routeId: route.id,
        group: group?.name ?? '',
        route: route.name,
        method: ingress.method,
        path: ingress.path,
        contentType: ingress.contentType,
        auth: ingress.auth.kind,
        tenantFrom: ingress.tenant,
        tenants: vendor?.tenantIds ?? [],
        issues: validateIngress(state, route, ingress),
      };
    });
};
